"use client";

import { useForm, Controller } from "react-hook-form";
import { ContactPageQueryResult } from "@/sanity.types";
import { FormData } from "./types";
import Dropdown from "./Dropdown";
import ButtonCta from "../../../utils/button-cta/ButtonCta";
import { useState } from "react";
import clsx from "clsx";
import { hardcodedContent } from "@/app/(site)/_lib/global-content";
import Link from "next/link";
import { ROUTES } from "@/app/(site)/_lib/routes";
import Loader from "../../../utils/loader/Loader";

type FormProps = {
	formTopics: NonNullable<ContactPageQueryResult>["content"]["formTopics"];
};

export default function Form({ formTopics }: FormProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
		reset,
		watch,
	} = useForm<FormData>({
		defaultValues: {
			topic: "",
			nameAndSurname: "",
			telephoneNumber: "",
			email: "",
			companyName: "",
			message: "",
			acceptPrivacyPolicy: false,
		},
	});

	const topicOptions =
		formTopics?.map((topic) => ({
			value: topic.topic,
			label: topic.topic,
		})) || [];

	const onSubmit = async (data: FormData) => {
		setIsSubmitting(true);
		setSubmitStatus("idle");

		try {
			const response = await fetch("/api/contact", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(data),
			});

			if (response.ok) {
				setSubmitStatus("success");
				reset();
			} else {
				setSubmitStatus("error");
			}
		} catch (error) {
			console.error("Form submission error:", error);
			setSubmitStatus("error");
		} finally {
			setIsSubmitting(false);
		}
	};

	const inputBaseClass =
		"peer w-full pt-6 pb-2 px-3 text-sm text-black bg-middle-light rounded-md placeholder-transparent transition-all focus:outline-none ";

	const getInputClass = (fieldName: keyof FormData) => {
		return clsx(inputBaseClass, {
			"border border-error": errors[fieldName],
			"border-none": !errors[fieldName],
		});
	};

	const getLabelClass = (hasValue: boolean) => {
		return clsx(
			"pointer-events-none absolute left-3 text-[0.625rem] text-graphite-black/40 transition-all",
			"peer-placeholder-shown:top-[0.9375rem] peer-placeholder-shown:text-body-m peer-placeholder-shown:text-graphite-black/40",
			"peer-focus:text-text-graphite-black/40 peer-focus:top-2 peer-focus:text-[0.625rem]",
			hasValue && "text-graphite-black top-2 text-body-m",
		);
	};

	const nameValue = watch("nameAndSurname", "");
	const telephoneValue = watch("telephoneNumber", "");
	const emailValue = watch("email", "");
	const companyValue = watch("companyName", "");
	const messageValue = watch("message", "");

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="space-y-1.5">
			{/* Topic Dropdown */}
			<div>
				<Controller
					name="topic"
					control={control}
					rules={{ required: hardcodedContent.contactPage.formMessage.topic }}
					render={({ field }) => (
						<Dropdown
							id="topic"
							title="Topic"
							options={topicOptions}
							selectedOption={field.value}
							onSelect={field.onChange}
							style={clsx({
								"border-error": errors.topic,
							})}
						/>
					)}
				/>
				{errors.topic && <p className="text-body-s text-error mt-1">{errors.topic.message}</p>}
			</div>

			{/* Name and Surname */}
			<div className="relative">
				<input
					type="text"
					id="nameAndSurname"
					{...register("nameAndSurname", {
						required: "Name and surname is required",
						minLength: { value: 2, message: "Minimum 2 characters" },
					})}
					placeholder="Name and Surname"
					className={getInputClass("nameAndSurname")}
				/>
				<label htmlFor="nameAndSurname" className={getLabelClass(nameValue.length > 0)}>
					Name and Surname
				</label>
				{errors.nameAndSurname && <p className="text-body-s text-error mt-1">{errors.nameAndSurname.message}</p>}
			</div>

			{/* Telephone Number */}
			<div className="relative">
				<input
					{...register("telephoneNumber", {
						required: hardcodedContent.contactPage.formMessage.telephoneNumber,
						pattern: {
							value: /^[\+]?[0-9\s\-\(\)]{9,}$/,
							message: hardcodedContent.contactPage.formMessage.telephoneNumberInvalid,
						},
					})}
					type="tel"
					id="telephoneNumber"
					placeholder="Telephone number"
					className={getInputClass("telephoneNumber")}
				/>
				<label htmlFor="telephoneNumber" className={getLabelClass(telephoneValue.length > 0)}>
					Telephone number
				</label>
				{errors.telephoneNumber && <p className="text-body-s text-error mt-1">{errors.telephoneNumber.message}</p>}
			</div>

			{/* E-mail */}
			<div className="relative">
				<input
					{...register("email", {
						required: hardcodedContent.contactPage.formMessage.email,
						pattern: {
							value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
							message: hardcodedContent.contactPage.formMessage.emailInvalid,
						},
					})}
					type="email"
					id="email"
					placeholder="E-mail"
					className={getInputClass("email")}
				/>
				<label htmlFor="email" className={getLabelClass(emailValue.length > 0)}>
					E-mail
				</label>
				{errors.email && <p className="text-body-s text-error mt-1">{errors.email.message}</p>}
			</div>

			{/* Company Name (Optional) */}
			<div className="relative">
				<input
					{...register("companyName")}
					type="text"
					id="companyName"
					placeholder="Company name"
					className={getInputClass("companyName")}
				/>
				<label htmlFor="companyName" className={getLabelClass((companyValue || "").length > 0)}>
					Company name
				</label>
			</div>

			{/* Message */}
			<div className="relative">
				<textarea
					{...register("message", {
						required: hardcodedContent.contactPage.formMessage.message,
						minLength: {
							value: 10,
							message: hardcodedContent.contactPage.formMessage.messageMinLength,
						},
					})}
					id="message"
					placeholder="Message"
					rows={6}
					className={clsx(getInputClass("message"), "resize-none")}
				/>
				<label htmlFor="message" className={getLabelClass(messageValue.length > 0)}>
					Message
				</label>
				{errors.message && <p className="text-body-s text-error mt-1">{errors.message.message}</p>}
			</div>

			{/* Privacy Policy Checkbox */}
			<div className="xxs:flex-row xxs:gap-1 flex flex-col items-center justify-between gap-4 pt-1.5">
				<div className="xxs:max-w-[14.063rem]">
					<label className="xs2:gap-3 xxs:gap-1 flex cursor-pointer items-start gap-2">
						<input
							{...register("acceptPrivacyPolicy", {
								required: hardcodedContent.contactPage.formMessage.acceptPrivacyPolicy,
							})}
							type="checkbox"
							className="h-4 w-4 rounded-lg"
						/>
						<span className="text-cap-s text-graphite-black/70">
							{hardcodedContent.contactPage.formMessage.privacyPolicyInfo}{" "}
							<Link
								href={ROUTES.PRIVACY_POLICY}
								className="hover:text-navy-blue-deep underline transition-colors duration-300"
							>
								{hardcodedContent.contactPage.formMessage.privacyPolicyTerms}
							</Link>
						</span>
					</label>
					{errors.acceptPrivacyPolicy && <p className="text-body-s text-error mt-1">{errors.acceptPrivacyPolicy.message}</p>}
				</div>

				{/* Submit Button */}
				<div className="">
					{isSubmitting ? (
						<Loader />
					) : (
						<ButtonCta type="submit" label={"SEND MESSAGE"} disabled={isSubmitting} className="w-full cursor-pointer" />
					)}
				</div>
			</div>

			{/* Status Messages */}
			{submitStatus === "success" && (
				<div className="border-success bg-success rounded-lg border p-4">
					<p className="text-body-s text-white">{hardcodedContent.contactPage.formMessage.success}</p>
				</div>
			)}

			{submitStatus === "error" && (
				<div className="border-error bg-error rounded-lg border p-4">
					<p className="text-body-s text-white">{hardcodedContent.contactPage.formMessage.error}</p>
				</div>
			)}
		</form>
	);
}
